import { notification } from "antd";
import {
  autorun,
  IReactionDisposer,
  makeAutoObservable,
  runInAction,
} from "mobx";

import { AgentAPI } from "@/api";
import { AgentUpdated } from "@/events";
import Modal from "@/models/modal";
import Query from "@/models/query";
import { TAgentCreateSchema } from "@/schemas";

import { DisposableStore } from "..";
import { SelectorStore } from "../selectors";

import { AgentsStore } from ".";

export class AgentCreateStore implements DisposableStore {
  constructor(public readonly agentsStore: AgentsStore) {
    makeAutoObservable(this);

    const disposeAutorun = autorun(() => {
      this.initializeParentAgentSelection();
    });

    this.disposers.push(disposeAutorun);
  }

  readonly disposers: IReactionDisposer[] = [];

  resetState = () => {
    this.createQuery.reset();
    this.parentAgentSelectorStore.setSelectedId(undefined);
  };

  dispose = () => {
    this.disposers.forEach((dispose) => dispose());
    this.resetState();
  };

  initializeParentAgentSelection = () => {
    const clientId = this.clientSelectorStore.selectedId;
    const agentId = this.agentSelectorStore.selectedId;
    const subagentId = this.subagentSelectorStore.selectedId;

    runInAction(() => {
      if (this.permissionsStore.has("SelectSubagent") && agentId) {
        this.parentAgentSelectorStore.setParameters({
          parentAgentId: agentId,
        });
        this.parentAgentSelectorStore.setSelectedId(subagentId);
        this.parentAgentSelectorStore.fetchItems();
      } else if (this.permissionsStore.has("SelectAgent") && clientId) {
        this.parentAgentSelectorStore.setParameters({ clientId });
        this.parentAgentSelectorStore.setSelectedId(agentId);
        this.parentAgentSelectorStore.fetchItems();
      }
    });
  };

  get userStore() {
    return this.agentsStore.globalStore.userStore;
  }

  get permissionsStore() {
    return this.agentsStore.globalStore.permissionsStore;
  }

  get eventBusService() {
    return this.agentsStore.globalStore.eventBusService;
  }

  get clientSelectorStore() {
    return this.agentsStore.generalSelectorsStore.clientSelectorStore;
  }

  get agentSelectorStore() {
    return this.agentsStore.generalSelectorsStore.agentSelectorStore;
  }

  get subagentSelectorStore() {
    return this.agentsStore.generalSelectorsStore.subagentSelectorStore;
  }

  get intl() {
    return this.agentsStore.globalStore.intlService.intl;
  }

  get isBalanceUnlimitedDisabled() {
    return (
      this.parentAgentSelectorStore.selectedItem?.isBalanceUnlimited ===
        false || this.userStore.agent?.isBalanceUnlimited === false
    );
  }

  get parentCurrency() {
    if (
      typeof this.parentAgentSelectorStore.selectedItem?.currency === "string"
    ) {
      return this.parentAgentSelectorStore.selectedItem.currency;
    }
    if (typeof this.subagentSelectorStore.selectedItem?.currency === "string") {
      return this.subagentSelectorStore.selectedItem.currency;
    }
    if (typeof this.agentSelectorStore.selectedItem?.currency === "string") {
      return this.agentSelectorStore.selectedItem.currency;
    }
    if (typeof this.clientSelectorStore.selectedItem?.currency === "string") {
      return this.clientSelectorStore.selectedItem?.currency;
    }
    if (typeof this.userStore.agent?.currency === "string") {
      return this.userStore.agent.currency;
    }
    return null;
  }

  parentAgentSelectorStore = new SelectorStore({
    filterMethod: AgentAPI.filter,
    getByIdMethod: AgentAPI.getById,
    labelKey: "name",
  });

  modal = new Modal();
  modalShort = new Modal();

  createQuery = new Query(AgentAPI.create);

  handleSubmit = async (values: TAgentCreateSchema) => {
    if (!this.clientSelectorStore.selectedId) {
      return;
    }

    const parentAgentId =
      this.parentAgentSelectorStore.selectedId ?? this.userStore.agent?.id;

    await this.createQuery.submit({
      clientId: this.clientSelectorStore.selectedId,
      parentAgentId,
      ...values,
    });

    if (!this.createQuery.isFulfilled) {
      return;
    }

    notification.success({
      message: "",
      description: this.intl.formatMessage({
        defaultMessage: "The agent has been successfully created.",
      }),
    });

    this.modal.close();
    this.modalShort.close();

    const createdAgent = this.createQuery.data;
    this.eventBusService.publish(
      new AgentUpdated({
        agentId: createdAgent.id,
      }),
    );

    this.agentsStore.filter();
  };
}
